import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useMediaQuery } from '@/lib/useMediaQuery';
import { SOURCE_LABELS, type FieldRow, type Sighting } from '@/papermill/extraction';
import { formatClock } from '@/papermill/transcript';

import styles from './EntityDetail.module.css';

type Section = 'source' | 'relationships';

/** Below this the page and relationships share one slot, one open at a time. */
const NARROW = '(max-width: 760px)';

function Chevron({ open }: { open: boolean }) {
  return (
    <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden="true">
      ›
    </span>
  );
}

export interface EntityRelationship {
  label: string;
  direction: 'out' | 'in';
  otherId: string;
  otherLabel: string;
}

export interface EntityDetailProps {
  fields: FieldRow[];
  relationships: EntityRelationship[];
  onOpenEntity: (id: string) => void;
  /** Draws the page, or the moment, a value came from; the viewer is the caller's. */
  renderSource: (sighting: Sighting & { label: string }) => ReactNode;
  /** What the values were read from, in the copy: a document or a recording. */
  medium?: 'document' | 'recording';
  /** The attribute to open on, e.g. the value tapped in a transcript. */
  initialField?: string;
}

/** "page 2", or "at 1:04" for a moment in a recording. */
function whereOf(sighting: Sighting): string {
  return sighting.span ? `at ${formatClock(sighting.span.start_ms)}` : `page ${sighting.page + 1}`;
}

function Confidence({ value }: { value: number }) {
  const percent = Math.round(value * 100);
  const tone = value >= 0.85 ? styles.high : value >= 0.6 ? styles.medium : styles.low;
  return (
    <span className={styles.confidence} title={`${percent}% read confidence`}>
      <span className={styles.meter} aria-hidden="true">
        <span className={tone} style={{ width: `${percent}%` }} />
      </span>
      {percent}%
    </span>
  );
}

/** What was read about one entity, and where each value came from. */
export function EntityDetail({
  fields,
  relationships,
  onOpenEntity,
  renderSource,
  medium = 'document',
  initialField,
}: EntityDetailProps) {
  const firstGrounded =
    fields.find((field) => field.key === initialField)?.key ??
    fields.find((field) => field.grounded)?.key ??
    null;
  const [selected, setSelected] = useState<string | null>(firstGrounded);
  const [sightingIndex, setSightingIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const box = listRef.current;
    const pressed = box?.querySelector('[aria-pressed="true"]');
    if (box && pressed instanceof HTMLElement && pressed.offsetTop > box.clientHeight) {
      box.scrollTop = pressed.offsetTop - box.clientHeight / 3;
    }
  }, []);
  const field = fields.find((candidate) => candidate.key === selected) ?? null;
  const sighting = field?.sightings[Math.min(sightingIndex, field.sightings.length - 1)];
  const narrow = useMediaQuery(NARROW);
  const [open, setOpen] = useState<Section | null>(firstGrounded ? 'source' : 'relationships');
  const sourceOpen = !narrow || open === 'source';
  const relationshipsOpen = !narrow || open === 'relationships';

  function choose(key: string) {
    setSelected(key);
    setSightingIndex(0);
    setOpen('source');
  }

  function toggle(section: Section) {
    setOpen(open === section ? null : section);
  }

  return (
    <div className={styles.layout}>
      <div className={styles.column}>
        <div className={styles.attributes}>
          <h3 className={styles.heading}>Extracted attributes</h3>
          <div ref={listRef} className={styles.scrollBox}>
            {fields.length === 0 ? (
              <p className={styles.muted}>Nothing was read for this entity.</p>
            ) : (
              <ul className={styles.fields} aria-label="Extracted attributes">
                {fields.map((row) => (
                  <li key={row.key}>
                    <button
                      type="button"
                      className={`${styles.field} ${row.key === selected ? styles.selected : ''}`}
                      aria-pressed={row.key === selected}
                      onClick={() => choose(row.key)}
                    >
                      <span className={styles.fieldLabel}>{row.label}</span>
                      <span className={styles.fieldValue}>
                        {row.value}
                        {row.corrected ? <span className={styles.chip}>corrected</span> : null}
                      </span>
                      <span className={styles.fieldMeta}>
                        <Confidence value={row.confidence} />
                        <span className={styles.chip}>
                          {SOURCE_LABELS[row.source] ?? row.source}
                        </span>
                        {row.grounded ? (
                          <span className={styles.located} aria-label="Has a source location">
                            {row.sightings[0].span
                              ? `◷ ${formatClock(row.sightings[0].span.start_ms)}`
                              : `◎ p.${row.sightings[0].page + 1}`}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {relationships.length > 0 ? (
          <div className={styles.relationshipsBlock}>
            {narrow ? (
              <button
                type="button"
                className={styles.accordion}
                aria-expanded={relationshipsOpen}
                onClick={() => toggle('relationships')}
              >
                <span>
                  Relationships <span className={styles.count}>{relationships.length}</span>
                </span>
                <Chevron open={relationshipsOpen} />
              </button>
            ) : (
              <h3 className={styles.heading}>Relationships</h3>
            )}
            {relationshipsOpen ? (
              <ul className={`${styles.relationships} ${styles.relationshipBox}`}>
                {relationships.map((relationship, index) => (
                  <li key={`${relationship.otherId}-${index}`}>
                    <span className={styles.muted}>
                      {relationship.direction === 'out'
                        ? relationship.label
                        : `${relationship.label} ←`}
                    </span>
                    <button
                      type="button"
                      className={styles.link}
                      onClick={() => onOpenEntity(relationship.otherId)}
                    >
                      {relationship.otherLabel}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className={`${styles.column} ${styles.sourceColumn} ${field ? '' : styles.idle}`}>
        <h3 className={styles.heading}>
          {medium === 'recording' ? 'Where it was said' : 'Where it was read'}
        </h3>
        {!field ? (
          <p className={styles.placeholder}>
            Select an attribute to see where it came from in the {medium}.
          </p>
        ) : (
          <div className={styles.source}>
            <div className={styles.sourceHeader}>
              {narrow ? (
                <button
                  type="button"
                  className={styles.accordion}
                  aria-expanded={sourceOpen}
                  onClick={() => toggle('source')}
                >
                  <span>
                    <strong>{field.label}</strong>
                    {sighting ? ` · ${whereOf(sighting)}` : ' · no location'}
                  </span>
                  <Chevron open={sourceOpen} />
                </button>
              ) : sighting ? (
                <span>
                  <strong>{field.label}</strong> · {whereOf(sighting)}
                </span>
              ) : null}
              {sourceOpen && sighting && field.sightings.length > 1 ? (
                <span className={styles.pager}>
                  <button
                    type="button"
                    aria-label="Previous sighting"
                    disabled={sightingIndex === 0}
                    onClick={() => setSightingIndex(sightingIndex - 1)}
                  >
                    ‹
                  </button>
                  {sightingIndex + 1} of {field.sightings.length}
                  <button
                    type="button"
                    aria-label="Next sighting"
                    disabled={sightingIndex >= field.sightings.length - 1}
                    onClick={() => setSightingIndex(sightingIndex + 1)}
                  >
                    ›
                  </button>
                </span>
              ) : null}
            </div>
            {!sourceOpen ? null : sighting ? (
              <>
                <div className={`${styles.viewer} ${sighting.span ? styles.sliceViewer : ''}`}>
                  {renderSource({ ...sighting, label: field.label })}
                </div>
                {sighting.evidence && !sighting.span ? (
                  <blockquote className={styles.quote}>“{sighting.evidence}”</blockquote>
                ) : null}
              </>
            ) : (
              <div className={styles.placeholder}>
                <strong>{field.label}</strong>{' '}
                {medium === 'recording'
                  ? 'has no moment in the recording — it was inferred rather than heard directly.'
                  : 'has no location on the page — it was inferred rather than read directly.'}
                {field.evidence ? (
                  <blockquote className={styles.quote}>“{field.evidence}”</blockquote>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
